// eslint-disable-next-line import/no-cycle
import { adminApi } from '@/includes/contentManagementApi';

/**
 * Level System Service
 * Handles all API calls related to the level system management
 */
export default {
  // Registry Management
  /**
   * Initialize the level system registry with default entries
   * @returns {Promise} Initialization response
   */
  initializeRegistry() {
    return adminApi.post('/level-system-registry/initialize');
  },

  /**
   * Get all registry entries
   * @returns {Promise} Registry entries response
   */
  getRegistryEntries() {
    return adminApi.get('/level-system-registry');
  },

  /**
   * Create a new registry entry
   * @param {Object} entryData - The registry entry data
   * @returns {Promise} Created entry response
   */
  createRegistryEntry(entryData) {
    return adminApi.post('/level-system-registry', entryData);
  },

  /**
   * Update an existing registry entry
   * @param {string} id - The registry entry ID
   * @param {Object} entryData - The updated entry data
   * @returns {Promise} Updated entry response
   */
  updateRegistryEntry(id, entryData) {
    return adminApi.put(`/level-system-registry/id/${id}`, entryData);
  },

  /**
   * Delete a registry entry
   * @param {string} id - The registry entry ID
   * @returns {Promise} Deletion response
   */
  deleteRegistryEntry(id) {
    return adminApi.delete(`/level-system-registry/id/${id}`);
  },

  // Level Targets Management
  /**
   * Get all level targets
   * @returns {Promise} Level targets response
   */
  getLevelTargets() {
    return adminApi.get('/level-targets');
  },

  /**
   * Create a new level target
   * @param {Object} levelData - The level target data
   * @returns {Promise} Created level response
   */
  createLevelTarget(levelData) {
    return adminApi.post('/level-targets', levelData);
  },

  /**
   * Update an existing level target
   * @param {string} id - The level target ID
   * @param {Object} levelData - The updated level data
   * @returns {Promise} Updated level response
   */
  updateLevelTarget(id, levelData) {
    return adminApi.put(`/level-targets/${id}`, levelData);
  },

  /**
   * Get level assignment status
   * @param {string} id - The level target ID
   * @returns {Promise} Assignment status response
   */
  getLevelAssignmentStatus(id) {
    return adminApi.get(`/level-targets/${id}/assignment-status`);
  },

  /**
   * Delete a level target with smart deletion options
   * @param {string} id - The level target ID
   * @param {string} deleteOption - 'soft' or 'cascade'
   * @returns {Promise} Deletion response
   */
  deleteLevelTarget(id, deleteOption) {
    return adminApi.delete(`/level-targets/${id}`, {
      data: { deleteOption },
    });
  },

  /**
   * Get a specific level target
   * @param {string} id - The level target ID
   * @returns {Promise} Level target response
   */
  getLevelTarget(id) {
    return adminApi.get(`/level-targets/${id}`);
  },

  /**
   * Get level system configuration (inactivity threshold, channels, stabilisation reminder)
   * @returns {Promise} Level system config response
   */
  getLevelSystemConfig() {
    return adminApi.get('/config/level-system');
  },

  /**
   * Update level system configuration
   * @param {Object} config - Level system config
   *  (inactivityThresholdDays, inactivityNotificationChannels,
   *  stabilisationReminderEnabled, stabilisationReminderIntervalDays)
   * @returns {Promise} Update response
   */
  updateLevelSystemConfig(config) {
    return adminApi.put('/config/level-system', config);
  },

  // User Progress Management
  /**
   * Get user statistics overview
   * @returns {Promise} User stats response
   */
  getUserStats() {
    return adminApi.get('/user-level-progress/stats');
  },

  /**
   * Assign a level to a user
   * @param {string} userId - The user ID
   * @param {number} levelNumber - The level number to assign
   * @returns {Promise} Assignment response
   */
  assignLevelToUser(userId, levelNumber) {
    return adminApi.post('/user-level-progress/assign', {
      userId,
      level: levelNumber,
    });
  },

  /**
   * Update user goal progress
   * @param {string} userId - The user ID
   * @param {number} level - The level number
   * @param {string} goalId - The goal ID
   * @param {number} progress - The progress value
   * @returns {Promise} Progress update response
   */
  updateUserGoalProgress(userId, level, goalId, progress) {
    return adminApi.put('/user-level-progress/progress', {
      userId,
      level,
      goalId,
      progress,
    });
  },

  /**
   * Get user progress summary
   * @param {string} userId - The user ID
   * @returns {Promise} Progress summary response
   */
  getUserProgressSummary(userId) {
    return adminApi.get(`/user-level-progress/summary/${userId}`);
  },

  /**
   * Get all levels for a user
   * @param {string} userId - The user ID
   * @returns {Promise} User levels response
   */
  getUserLevels(userId) {
    return adminApi.get(`/user-level-progress/${userId}`);
  },

  /**
   * Get a specific level for a user
   * @param {string} userId - The user ID
   * @param {number} level - The level number
   * @returns {Promise} User level response
   */
  getUserLevel(userId, level) {
    return adminApi.get(`/user-level-progress/${userId}/${level}`);
  },

  // Utility Methods
  /**
   * Validate a goal against registry rules
   * @param {Object} goalData - The goal data to validate
   * @returns {Promise} Validation response
   */
  validateGoal(goalData) {
    return adminApi.post('/level-system-registry/validate-goal', goalData);
  },

  /**
   * Get the next available level number
   * @returns {Promise} Next level number response
   */
  getNextLevelNumber() {
    return adminApi.get('/level-targets/next-number');
  },

  /**
   * Check if a level number already exists
   * @param {number} levelNumber - The level number to check
   * @returns {Promise} Existence check response
   */
  levelExists(levelNumber) {
    return adminApi.get(`/level-targets/exists/${levelNumber}`);
  },

  assignAllLevelsToUser(data) {
    return adminApi.post('/user-level-progress/assign-all-levels', data);
  },

  // Audit Log
  /**
   * Get audit logs with optional filters and pagination
   * @param {Object} params - { userId, activityType, eventType,
   *  startDate, endDate, page, limit, sortBy, sortOrder }
   * @returns {Promise} { data, total, page, limit, totalPages }
   */
  getAuditLogs(params = {}) {
    return adminApi.get('/level-system-audit-log', { params });
  },

  /**
   * Export audit logs to CSV with optional filters
   * @param {Object} params - { userId, activityType,
   *  eventType, startDate, endDate, sortBy, sortOrder }
   * @returns {Promise} Blob response for file download
   */
  exportAuditLogs(params = {}) {
    return adminApi.get('/level-system-audit-log/export', {
      params,
      responseType: 'blob',
    });
  },

  // User level history (Sub-task 3)
  getUserHistory(userId) {
    return adminApi.get(`/user-level-progress/${userId}/history`);
  },

  /**
   * Preview what recalculate would change (no changes applied). Sub-task 5.
   * @param {string} userId
   * @returns {Promise<{ userId, corrections, currentLevel, isConsistent }>}
   */
  recalculateUserLevelPreview(userId) {
    return adminApi.get(`/user-level-progress/recalculate-preview/${userId}`);
  },

  /**
   * Apply recalculate and fix user level state from goal progress (admin).
   * Uses the anomaly detector endpoint which also fixes behind goal progress from source data.
   * @param {string} userId
   * @returns {Promise<{ success, message, corrections, goalProgressCorrections, currentLevel }>}
   */
  recalculateUserLevel(userId) {
    return adminApi.post('/anomalies-detector/repair/recalculate', { userId });
  },

  getHistoryByEmail(userEmail) {
    return adminApi.get('/user-level-progress/history', { params: { userEmail } });
  },

  getGroupAverage() {
    return adminApi.get('/user-level-progress/group-average');
  },

  exportUserHistory(params = {}) {
    return adminApi.get('/user-level-progress/history/export', {
      params,
      responseType: 'blob',
    });
  },

  // Anomalies Detector
  scanAnomalies() {
    return adminApi.get('/anomalies-detector/scan');
  },

  getUserAnomalyDetails(userId) {
    return adminApi.get(`/anomalies-detector/user/${userId}`);
  },

  exportAnomaliesReport() {
    return adminApi.get('/anomalies-detector/export', {
      responseType: 'blob',
    });
  },

  repairSyncGoals(userId, levelNumber) {
    return adminApi.post('/anomalies-detector/repair/sync-goals', {
      userId,
      levelNumber,
    });
  },

  repairMarkCompleted(userId, levelNumber, comment) {
    return adminApi.post('/anomalies-detector/repair/mark-completed', {
      userId,
      levelNumber,
      comment,
    });
  },

  repairDeleteGoal(userId, levelNumber, goalId) {
    return adminApi.post('/anomalies-detector/repair/delete-goal', {
      userId,
      levelNumber,
      goalId,
    });
  },

  repairRecalculate(userId) {
    return adminApi.post('/anomalies-detector/repair/recalculate', {
      userId,
    });
  },

  repairAssignMissingLevels(userId) {
    return adminApi.post('/anomalies-detector/repair/assign-missing-levels', {
      userId,
    });
  },

  repairResetCurrentLevel(userId) {
    return adminApi.post('/anomalies-detector/repair/reset-current-level', {
      userId,
    });
  },

  repairFixGoalProgress(userId, levelNumber, goalId, includeHistorical = false) {
    return adminApi.post('/anomalies-detector/repair/fix-goal-progress', {
      userId,
      levelNumber,
      goalId,
      includeHistorical: !!includeHistorical,
    });
  },

  repairFixAllGoalProgress(userId) {
    return adminApi.post('/anomalies-detector/repair/fix-all-goal-progress', {
      userId,
    });
  },

  repairBulkAll(repairTypes) {
    return adminApi.post('/anomalies-detector/repair/bulk', {
      repairTypes,
    });
  },

  /** Dry-run: duplicate goal IDs across all levels (Developer tab). */
  previewDuplicateGoalsRepair() {
    return adminApi.get('/anomalies-detector/repair/duplicate-goals/preview');
  },

  /** Apply duplicate goal ID repair (unique ids + progress fix). */
  executeDuplicateGoalsRepair() {
    return adminApi.post('/anomalies-detector/repair/duplicate-goals/execute');
  },
};
